/**
 * ⚠⚠ `#669` — **CE GARDE NE PASSE PLUS PAR `useAuth`, ET C'EST UNE DETTE QUE LA
 *   VAGUE A RÉVÉLÉE.**
 *
 *   *`useAuth` appelle `useNavigate()` en tête — pour son `logout`, dont ce
 *   composant n'a **aucun besoin**. Conséquence : **toute surface portant une
 *   permission exigeait un `<Router>`**, y compris dans un banc qui ne rend
 *   qu'un tableau. La dépendance était **incidente**, jamais voulue.*
 *
 * ⚠ **MESURÉ EN BRANCHANT LE LAB** : trente bancs du module ont viré au rouge
 *   d'un coup, tous sur `useNavigate() may be used only in the context of a
 *   <Router>`. *Sept paliers restent derrière — la dette se paie ici, une fois.*
 *
 * ⚠ Les deux fonctions viennent **du même store** qu'avant : rien ne change du
 *   comportement, seul le chemin d'accès se raccourcit.
 */
import { useUserStore } from '@/stores/userStore';
import type { ReactNode } from 'react';

interface HasPermissionProps {
  /** Permission(s) à vérifier */
  permission: string | string[];
  /** Contenu rendu si autorisé */
  children: ReactNode;
  /** Contenu alternatif si non autorisé (défaut : null) */
  fallback?: ReactNode;
  /** Si true, AND logic (toutes requises). Défaut OR. */
  requireAll?: boolean;
}

/**
 * HasPermission — guard d'affichage conditionnel.
 *
 * Usage :
 *   <HasPermission permission="querybuilder.use">
 *     <QueryBuilderLink />
 *   </HasPermission>
 *
 *   <HasPermission permission={['admin.edit', 'admin.view']} requireAll fallback={<ReadOnly />}>
 *     <AdminPanel />
 *   </HasPermission>
 */
export function HasPermission({
  permission,
  children,
  fallback = null,
  requireAll = false,
}: HasPermissionProps) {
  const hasPermission = useUserStore((s) => s.hasPermission);
  const hasAllPermissions = useUserStore((s) => s.hasAllPermissions);

  const perms = Array.isArray(permission) ? permission : [permission];
  const allowed = requireAll ? hasAllPermissions(perms) : hasPermission(perms);

  return <>{allowed ? children : fallback}</>;
}
